import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from 'react';

// ─── Tour Step Definition ──────────────────────────────────────────────────────

export interface TourStep {
  id: string;
  /** data-tour attribute value to find the target element */
  targetSelector: string;
  /** Page to navigate to before highlighting this step */
  page: string;
  title: string;
  description: string;
  /** Preferred tooltip placement relative to the highlighted element */
  placement: 'top' | 'bottom' | 'left' | 'right' | 'center';
  /** Icon name (Lucide) for step header */
  icon: string;
  /**
   * Extra delay (ms) added before the overlay starts measuring the target.
   * Use for steps that cross page boundaries or wait for animations to settle.
   */
  pageDelay?: number;
}

// ─── Tour Steps Config ─────────────────────────────────────────────────────────

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    targetSelector: '',
    page: 'calendar',
    title: 'Welcome to Scheduler',
    description:
      'This guided tour will walk you through the key features of Scheduler in just a few steps. Each step highlights a different part of the application — use the Next and Back buttons or your arrow keys to move through at your own pace.',
    placement: 'center',
    icon: 'Wand2',
  },
  {
    id: 'navigation',
    targetSelector: '[data-tour="navigation"]',
    page: 'calendar',
    title: 'Navigation Menu',
    description:
      'The sidebar is your primary way to move around Scheduler. It includes: Homepage, Planner, Heatmap, Open Demand, Resources, Projects, Reports, Settings, Company Switcher, and Help & Tour. The sidebar collapses to icon-only mode to give you more screen space when you need it.',
    placement: 'right',
    icon: 'LayoutDashboard',
  },
  {
    id: 'overview-kpis',
    targetSelector: '[data-tour="overview-kpis"]',
    page: 'overview',
    title: 'Dashboard Overview',
    description:
      'The Dashboard Overview is your morning snapshot — a single screen that tells you the health of your team before you open the planner. The five KPI cards across the top show Total Resources, Team Utilisation, Open Demand, Burnout Risk, and Budget Health, each with a live tooltip explaining how the metric is calculated. Secondary cards below surface Forecast Utilisation, Available Capacity, Bench Resources, and Coming Free — giving you a forward-looking view alongside today\'s position.',
    placement: 'bottom',
    icon: 'BarChart2',
    pageDelay: 600,
  },
  {
    id: 'overview-trend',
    targetSelector: '[data-tour="overview-trend"]',
    page: 'overview',
    title: 'Utilisation Trend & Alerts',
    description:
      'The Utilisation Trend chart shows your team\'s average weekly utilisation over the last eight weeks, colour-coded against the target thresholds configured in Settings. Below the KPIs, contextual alert banners appear when action is needed — a Burnout Risk alert with a direct link to Heatmap resolution, and an Upcoming Availability notice when resources are coming free so you can plan ahead and avoid bench time.',
    placement: 'top',
    icon: 'TrendingUp',
  },
  {
    id: 'planner-calendar',
    targetSelector: '[data-tour="planner-calendar"]',
    page: 'calendar',
    title: 'Planner Calendar',
    description:
      'The Planner is your central scheduling workspace. Drag and drop bookings directly onto the timeline to assign resources, or click any empty slot to create a new booking. Bookings are colour-coded by status so you can read the schedule at a glance.',
    placement: 'bottom',
    icon: 'CalendarDays',
  },
  {
    id: 'planner-timeline-views',
    targetSelector: '[data-tour="planner-toolbar"]',
    page: 'calendar',
    title: 'Timeline Views',
    description:
      'Use the date navigator and view toggles at the top of the Planner to switch between Day, Week, Month, Quarter, and Year perspectives — making it easy to zoom in on a specific day or plan across a longer horizon. Use the zoom controls to adjust the column density within your chosen view.',
    placement: 'bottom',
    icon: 'LayoutList',
  },
  {
    id: 'planner-grouping',
    targetSelector: '[data-tour="planner-grouping"]',
    page: 'calendar',
    title: 'Resource Grouping',
    description:
      'The Group By dropdown in the top-left of the timeline lets you reorganise how resources are displayed. Switch between Customer, Project, Resource, or Role to change the row groupings instantly — great for focusing on a specific client, checking workload by role, or getting a clean per-person view.',
    placement: 'right',
    icon: 'Layers',
  },
  {
    id: 'booking-form',
    targetSelector: '',
    page: 'calendar',
    title: 'Booking Form',
    description:
      'The booking form slides in from the right and is organised into sections: Booking Type, Status, Schedule (Full Day, Morning, Afternoon, or Hourly — plus start/end dates and Repeat), Project/Customer (customer auto-populates if set against the project), and Resources. Open it via the toolbar Add Booking button, a single click on the timeline, click and drag to pre-fill the date range, or the right-click context menu on any empty cell.',
    placement: 'center',
    icon: 'PlusCircle',
  },
  {
    id: 'suggest-resource',
    targetSelector: '[data-tour="suggest-resource-dialog"]',
    page: 'calendar',
    title: 'Suggest Resource',
    description:
      'The recommendation engine analyses the booking\'s required role, skills, location, and date range — then ranks every available team member by match score and availability. The Suggestions tab shows your best-fit options instantly; the Browse All tab lets you search the full team. Select one or more and confirm to assign them directly to the booking.',
    placement: 'left',
    icon: 'Sparkles',
    pageDelay: 900,
  },
  {
    id: 'heatmap',
    targetSelector: '[data-tour="heatmap-grid"]',
    page: 'heatmap',
    title: 'Utilisation Heatmap',
    description:
      'The Heatmap gives you a bird\'s-eye view of resource utilisation across your entire team. Colour intensity reflects booking density — from light (under-utilised) to deep (fully booked or overloaded). Hover over any cell to see a detailed breakdown of hours booked, utilisation percentage, and booking split for that resource and period.',
    placement: 'bottom',
    icon: 'Activity',
    pageDelay: 800, // wait for booking form sheet to close + heatmap page to mount
  },
  {
    id: 'burnout-trends',
    targetSelector: '[data-tour="whatif-panel"]',
    page: 'heatmap',
    title: 'Burnout Trends',
    description:
      'When one or more resources are trending toward overload, this amber banner appears at the top of the Heatmap. It shows a live count of at-risk resources and lets you jump straight into resolution with a single click — before burnout affects project delivery.',
    placement: 'bottom',
    icon: 'AlertTriangle',
  },
  {
    id: 'conflict-resolution',
    targetSelector: '',
    page: 'heatmap',
    title: 'What-if Analysis',
    description:
      'Clicking "Resolve" on the Burnout Trends banner opens the What-if Analysis tool. It shows the overloaded resource\'s utilisation, lists resolution options — reassign to a team member, reduce hours, or delay the booking — and lets you model the impact of each choice before committing any change to the calendar.',
    placement: 'center',
    icon: 'GitMerge',
  },
  {
    id: 'open-demand-requests',
    targetSelector: '[data-tour="resource-requests-section"]',
    page: 'unallocated',
    title: 'Resource Requests',
    description:
      'Resource Requests is a planning wizard for inbound demand — before any bookings have been created. When a project manager raises a resource request, it appears here with details such as role, required skills, location, and target dates. Use the Plan button to open the resource planner, which proposes the best matching resources based on availability and skills, and lets you model different scenarios before converting them into confirmed bookings on the calendar.',
    placement: 'right',
    icon: 'Package',
  },
  {
    id: 'open-demand-unallocated',
    targetSelector: '[data-tour="unallocated-list"]',
    page: 'unallocated',
    title: 'Unallocated Bookings',
    description:
      'Unallocated Bookings shows bookings that have already been created but not yet assigned to a specific resource. Bookings are colour-coded by urgency — red for those starting within 3 days, amber for within 7 days. Use the Suggest Resource button to let the system match each booking to the best available resource based on skills and capacity, or use View in Planner to locate the booking directly on the calendar.',
    placement: 'right',
    icon: 'Inbox',
  },
  {
    id: 'resources',
    targetSelector: '[data-tour="resources-table"]',
    page: 'resources',
    title: 'Resource Directory',
    description:
      'The Resources page is your team directory. View capacity, utilisation rates, skills, and current assignments for every person. Use the filters to narrow by department, role, or availability, then click any resource to see their full schedule.',
    placement: 'bottom',
    icon: 'Users',
  },
  {
    id: 'projects',
    targetSelector: '[data-tour="projects-list"]',
    page: 'projects',
    title: 'Projects',
    description:
      'The Projects page gives you a full view of every project across your organisation — including status, priority, budget, allocated resources, and key dates. Switch between card and list views, filter by status or type, and click any project to drill into its full detail, bookings, and resource assignments.',
    placement: 'bottom',
    icon: 'FolderKanban',
  },
  {
    id: 'reports',
    targetSelector: '[data-tour="reports-suite"]',
    page: 'report-utilisation',
    title: 'Reports Suite',
    description:
      'The Reports suite offers six pre-built views: Utilisation Report, Utilisation Forecast, Available Capacity, Resource Allocation, Project Staffing, and Bench Report. Each report can be filtered by date range, project, and resource group, and exported to CSV.',
    placement: 'bottom',
    icon: 'BarChart2',
  },
  {
    id: 'settings',
    targetSelector: '[data-tour="settings"]',
    page: 'settings',
    title: 'Settings & Configuration',
    description:
      'Settings lets administrators configure booking types, working hours, planner groups, user roles, and integrations with external PSA systems. Non-admin users can manage their personal preferences and notification settings here.',
    placement: 'right',
    icon: 'Settings',
  },
];

// ─── Context Types ─────────────────────────────────────────────────────────────

interface TourContextValue {
  isActive: boolean;
  currentStepIndex: number;
  steps: TourStep[];
  voiceoverEnabled: boolean;
  startTour: () => void;
  resumeTour: () => void;
  endTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (index: number) => void;
  toggleVoiceover: () => void;
  /** Callback set by App.tsx so the tour can trigger navigation */
  setNavigationCallback: (cb: (page: string) => void) => void;
  /** Callback set by App.tsx so the tour can trigger step-specific actions (e.g. open WhatIf overlay) */
  setStepActionCallback: (cb: (stepId: string) => void) => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const TourContext = createContext<TourContextValue | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function TourProvider({ children }: { children: ReactNode }) {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [voiceoverEnabled, setVoiceoverEnabled] = useState(false);
  const navigationCallbackRef = useRef<((page: string) => void) | null>(null);
  const stepActionCallbackRef = useRef<((stepId: string) => void) | null>(null);

  const setNavigationCallback = useCallback((cb: (page: string) => void) => {
    navigationCallbackRef.current = cb;
  }, []);

  const setStepActionCallback = useCallback((cb: (stepId: string) => void) => {
    stepActionCallbackRef.current = cb;
  }, []);

  const navigateTo = useCallback((page: string) => {
    if (navigationCallbackRef.current) {
      navigationCallbackRef.current(page);
    }
  }, []);

  const fireStepAction = useCallback((stepId: string) => {
    if (stepActionCallbackRef.current) {
      stepActionCallbackRef.current(stepId);
    }
  }, []);

  /**
   * Picks the best available female en-GB voice from the browser's voice list.
   * Priority order:
   *   1. Google UK English Female
   *   2. Any en-GB voice with "female" in the name
   *   3. Any en-GB voice (Google tends to label these as female)
   *   4. Any en-US voice with "female" in the name (fallback)
   *   5. First available voice (last resort)
   */
  const pickVoice = useCallback((): SpeechSynthesisVoice | null => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return null;
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) return null;

    const lower = (s: string) => s.toLowerCase();

    // 1. Exact match — Google UK English Female
    const googleUkFemale = voices.find((v) =>
      lower(v.name).includes('google uk english female'),
    );
    if (googleUkFemale) return googleUkFemale;

    // 2. Any en-GB voice with "female" in the name
    const enGbFemale = voices.find(
      (v) => v.lang === 'en-GB' && lower(v.name).includes('female'),
    );
    if (enGbFemale) return enGbFemale;

    // 3. Any en-GB voice (browsers often default these to female)
    const enGb = voices.find((v) => v.lang === 'en-GB');
    if (enGb) return enGb;

    // 4. en-US female fallback
    const enUsFemale = voices.find(
      (v) => v.lang.startsWith('en') && lower(v.name).includes('female'),
    );
    if (enUsFemale) return enUsFemale;

    // 5. Any English voice
    const anyEnglish = voices.find((v) => v.lang.startsWith('en'));
    return anyEnglish ?? null;
  }, []);

  const speakStep = useCallback(
    (step: TourStep) => {
      if (!voiceoverEnabled || typeof window === 'undefined' || !window.speechSynthesis) return;
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(`${step.title}. ${step.description}`);
      utterance.lang = 'en-GB';
      utterance.rate = 0.92;
      utterance.pitch = 1.1;   // slightly higher pitch for a natural female tone

      const voice = pickVoice();
      if (voice) utterance.voice = voice;

      // Some browsers (Chrome) need a short delay after cancel() before speaking
      setTimeout(() => window.speechSynthesis.speak(utterance), 50);
    },
    [voiceoverEnabled, pickVoice],
  );

  const startTour = useCallback(() => {
    setCurrentStepIndex(0);
    setIsActive(true);
    navigateTo(TOUR_STEPS[0].page);
    fireStepAction(TOUR_STEPS[0].id);
  }, [navigateTo, fireStepAction]);

  const resumeTour = useCallback(() => {
    setIsActive(true);
    navigateTo(TOUR_STEPS[currentStepIndex].page);
    fireStepAction(TOUR_STEPS[currentStepIndex].id);
  }, [navigateTo, fireStepAction, currentStepIndex]);

  const endTour = useCallback(() => {
    setIsActive(false);
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStepIndex((prev) => {
      const next = prev + 1;
      if (next >= TOUR_STEPS.length) {
        setIsActive(false);
        if (typeof window !== 'undefined' && window.speechSynthesis) {
          window.speechSynthesis.cancel();
        }
        return prev;
      }
      navigateTo(TOUR_STEPS[next].page);
      fireStepAction(TOUR_STEPS[next].id);
      return next;
    });
  }, [navigateTo, fireStepAction]);

  const prevStep = useCallback(() => {
    setCurrentStepIndex((prev) => {
      const next = Math.max(0, prev - 1);
      navigateTo(TOUR_STEPS[next].page);
      fireStepAction(TOUR_STEPS[next].id);
      return next;
    });
  }, [navigateTo, fireStepAction]);

  const goToStep = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(TOUR_STEPS.length - 1, index));
      setCurrentStepIndex(clamped);
      navigateTo(TOUR_STEPS[clamped].page);
      fireStepAction(TOUR_STEPS[clamped].id);
    },
    [navigateTo, fireStepAction],
  );

  const toggleVoiceover = useCallback(() => {
    setVoiceoverEnabled((prev) => {
      if (prev && typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      return !prev;
    });
  }, []);

  // Speak when step changes (and voiceover is on)
  useEffect(() => {
    if (isActive && voiceoverEnabled) {
      const step = TOUR_STEPS[currentStepIndex];
      if (step) speakStep(step);
    }
  }, [isActive, currentStepIndex, voiceoverEnabled, speakStep]);

  // Re-speak once voices have loaded (Chrome defers the voice list until
  // after the first speechSynthesis call — voiceschanged fires when ready)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;

    const handleVoicesChanged = () => {
      if (isActive && voiceoverEnabled) {
        const step = TOUR_STEPS[currentStepIndex];
        if (step) speakStep(step);
      }
    };

    window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);
    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
    };
  }, [isActive, voiceoverEnabled, currentStepIndex, speakStep]);

  const value: TourContextValue = {
    isActive,
    currentStepIndex,
    steps: TOUR_STEPS,
    voiceoverEnabled,
    startTour,
    resumeTour,
    endTour,
    nextStep,
    prevStep,
    goToStep,
    toggleVoiceover,
    setNavigationCallback,
    setStepActionCallback,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used inside TourProvider');
  return ctx;
}
